import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { useEffect } from 'react';
import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { Ajax } from '_utils';
import * as Common from '_common';
/* eslint-disable @typescript-eslint/no-explicit-any */
export const SpaContext = React.createContext({});
SpaContext.displayName = 'SpaContext';
export function SpaContextProvider(props) {
    const user = Common.useUser();
    const canConnect = user && !user.isPublic;
    const [state, setState] = React.useState({});
    const connectionRef = React.useRef(null);
    const refreshIntervalRef = React.useRef(null);
    const reconnectTimeoutRef = React.useRef(null);
    const refreshInProgressRef = React.useRef(false);
    const isStoppingRef = React.useRef(false);
    useEffect(() => {
        isStoppingRef.current = false;
        startConnection();
        const handleBrowserResume = () => {
            if (document.hidden) {
                return;
            }
            void startConnection();
            void refreshCompleteState();
        };
        const handleOnline = () => {
            void startConnection();
            void refreshCompleteState();
        };
        window.addEventListener('focus', handleBrowserResume);
        window.addEventListener('online', handleOnline);
        document.addEventListener('visibilitychange', handleBrowserResume);
        return () => {
            window.removeEventListener('focus', handleBrowserResume);
            window.removeEventListener('online', handleOnline);
            document.removeEventListener('visibilitychange', handleBrowserResume);
            void endConnection();
        };
    }, []);
    function ensureRefreshInterval() {
        if (!refreshIntervalRef.current) {
            refreshIntervalRef.current = setInterval(() => {
                void refreshCompleteState();
            }, 60 * 1000);
        }
    }
    function clearReconnectTimeout() {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    }
    function scheduleReconnect(delayMs = 5000) {
        if (isStoppingRef.current || reconnectTimeoutRef.current) {
            return;
        }
        reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            void startConnection();
        }, delayMs);
    }
    function getOrCreateConnection() {
        if (!connectionRef.current) {
            const connection = new HubConnectionBuilder()
                .withUrl(Common.debugSettings.apiRoot + '/api/messages')
                .withAutomaticReconnect()
                .build();
            connection.on('StateChanged', (partialValue) => {
                try {
                    setState((prevState) => setContextProperty(prevState, partialValue));
                }
                catch (e) {
                    console.error(e);
                }
            });
            connection.onreconnected(() => {
                void refreshCompleteState();
            });
            connection.onclose((err) => {
                if (isStoppingRef.current) {
                    return;
                }
                if (err) {
                    console.error(err);
                }
                scheduleReconnect();
            });
            connectionRef.current = connection;
        }
        return connectionRef.current;
    }
    async function startConnection() {
        ensureRefreshInterval();
        if (!canConnect) {
            return;
        }
        const connection = getOrCreateConnection();
        clearReconnectTimeout();
        if (connection.state === HubConnectionState.Connected ||
            connection.state === HubConnectionState.Connecting ||
            connection.state === HubConnectionState.Reconnecting) {
            return;
        }
        try {
            await connection.start();
            await refreshCompleteState();
        }
        catch (err) {
            console.error(err);
            scheduleReconnect();
        }
    }
    async function endConnection() {
        isStoppingRef.current = true;
        clearReconnectTimeout();
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
        }
        const connection = connectionRef.current;
        connectionRef.current = null;
        if (connection) {
            try {
                await connection.stop();
            }
            catch (err) {
                console.error(err);
            }
        }
    }
    async function refreshCompleteState() {
        if (refreshInProgressRef.current) {
            return;
        }
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            return;
        }
        refreshInProgressRef.current = true;
        try {
            const spaContext = await Ajax.get('/api/spa');
            if (!spaContext) {
                return;
            }
            const mappedContext = mapProperties(spaContext);
            setState(mappedContext);
        }
        catch (ex) {
            console.error(ex);
        }
        finally {
            refreshInProgressRef.current = false;
        }
    }
    return _jsx(SpaContext.Provider, { value: state, children: props.children });
}
export function useSpaContext() {
    const state = React.useContext(SpaContext);
    return state;
}
export function setContextProperty(current, newValues) {
    try {
        const result = { ...current };
        // recursively merge the new values into the current context
        for (const key in newValues) {
            const normKey = normalizeKey(key);
            const nextValue = newValues[key];
            if (isObjectRecord(nextValue)) {
                const currentValue = isObjectRecord(current[normKey]) ? current[normKey] : {};
                if (!isObjectRecord(current[normKey])) {
                    result[normKey] = {};
                }
                result[normKey] = setContextProperty(currentValue, nextValue);
            }
            else {
                if (current[normKey] !== nextValue) {
                    result[normKey] = nextValue;
                }
            }
        }
        return result;
    }
    catch (e) {
        console.error(e);
        throw e;
    }
}
function normalizeKey(key) {
    if (!key || key.length === 0) {
        return '_';
    }
    if (key.length <= 3) {
        return key.toLowerCase();
    }
    // make first letter lowercase
    return key[0].toLowerCase() + key.substring(1);
}
function adjustPath(path) {
    if (!path || path.length === 0) {
        return '';
    }
    let adjustedPath = path[0].toLowerCase() + path.substring(1);
    if (adjustedPath.length <= 3) {
        adjustedPath = adjustedPath.toLowerCase();
    }
    return adjustedPath;
}
function isObjectRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function mapProperties(object) {
    const result = {};
    for (const key in object) {
        let item = object[key];
        if (isObjectRecord(item)) {
            item = mapProperties(item);
        }
        result[adjustPath(key)] = item;
    }
    return result;
}
